/**
 * Mapea códigos del RPC `customers.apply_customer_command` a copy friendly.
 * El código interno se preserva para Sentry / ingestError.
 */
export function extractCustomerCommandCode(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const known = [
    'customer_credit_limit_exceeded',
    'customer_blocked',
    'customer_forbidden',
    'customer_not_found',
    'customer_amount_invalid',
    'customer_payment_exceeds_balance',
    'customer_payment_exceeds_sale_pending',
    'customerId_required',
    'operationId_required',
    'organization_required',
    'branch_id_required',
    'branch_forbidden',
    'sale_not_found',
    'fiado_integrations_disabled',
    'unsupported_customer_payload',
    'unsupported_operation',
    'movementId_required',
    'supervisor_override_invalid',
    'supervisor_override_required',
    'customer_payment_method_invalid',
    'customer_check_number_required',
  ];
  for (const code of known) {
    if (trimmed === code || trimmed.startsWith(`${code}:`) || trimmed.startsWith(`${code} `)) {
      return code;
    }
  }
  if (/module_permission_denied/i.test(trimmed)) return 'module_permission_denied';
  if (/supervisor_pin/i.test(trimmed)) return 'supervisor_pin_error';
  // assert_org_capability: 'capability not enabled: <code>'
  if (/capability not enabled/i.test(trimmed)) return 'capability_not_enabled';
  return null;
}

export type MappedCustomerCommandError = {
  code: string;
  userMessage: string;
  /** Errores de negocio esperados — no capturar como crash en Sentry. */
  expected: boolean;
};

export function mapCustomerCommandError(raw: string | null | undefined): MappedCustomerCommandError {
  const code = extractCustomerCommandCode(raw) ?? 'customer_command_failed';
  switch (code) {
    case 'customer_credit_limit_exceeded':
      return {
        code,
        userMessage: 'El consumo supera el límite de crédito. Pedí autorización de supervisor.',
        expected: true,
      };
    case 'customer_blocked':
      return {
        code,
        userMessage: 'Este cliente está bloqueado. No podés registrar consumos hasta desbloquearlo.',
        expected: true,
      };
    case 'customer_forbidden':
      return {
        code,
        userMessage: 'No podés operar este cliente desde tu organización.',
        expected: true,
      };
    case 'customer_not_found':
      return {
        code,
        userMessage: 'No encontramos ese cliente. Refrescá e intentá de nuevo.',
        expected: true,
      };
    case 'customer_amount_invalid':
      return {
        code,
        userMessage: 'Ingresá un importe mayor a cero.',
        expected: true,
      };
    case 'customer_payment_exceeds_balance':
      return {
        code,
        userMessage: 'El cobro no puede superar lo que debe el cliente.',
        expected: true,
      };
    case 'customer_payment_exceeds_sale_pending':
      return {
        code,
        userMessage: 'El cobro no puede superar el pendiente de esa venta.',
        expected: true,
      };
    case 'module_permission_denied':
      return {
        code,
        userMessage: 'No tenés permiso para esta acción. Pedile a un admin.',
        expected: true,
      };
    case 'fiado_integrations_disabled':
      return {
        code,
        userMessage: 'La cuenta corriente no está habilitada en este momento.',
        expected: true,
      };
    case 'customer_payment_method_invalid':
      return {
        code,
        userMessage: 'Ese medio de cobro no está disponible. Elegí otro.',
        expected: true,
      };
    case 'customer_check_number_required':
      return {
        code,
        userMessage: 'Ingresá el número del cheque.',
        expected: true,
      };
    case 'capability_not_enabled':
      return {
        code,
        userMessage: 'Esa función está apagada. Prendela en Funciones del negocio o pedile a un admin.',
        expected: true,
      };
    case 'supervisor_override_invalid':
    case 'supervisor_override_required':
    case 'supervisor_pin_error':
      return {
        code,
        userMessage: 'La autorización de supervisor no es válida. Pedí el PIN de nuevo.',
        expected: true,
      };
    case 'branch_id_required':
    case 'branch_forbidden':
      return {
        code,
        userMessage: 'No hay una sucursal válida seleccionada.',
        expected: true,
      };
    case 'operationId_required':
    case 'customerId_required':
    case 'organization_required':
    case 'sale_not_found':
    case 'movementId_required':
    case 'unsupported_customer_payload':
    case 'unsupported_operation':
      return {
        code,
        userMessage: 'No se pudo registrar el movimiento. Reintentá.',
        expected: false,
      };
    default: {
      const msg = (raw ?? '').trim();
      if (msg && !/^[a-z][a-z0-9_.:\s%-]+$/i.test(msg)) {
        return { code, userMessage: msg, expected: false };
      }
      return {
        code,
        userMessage: 'No se pudo registrar el movimiento. Reintentá.',
        expected: false,
      };
    }
  }
}
